/*
  The events both platforms send, as names and property shapes.

  Analytics is one funnel measured across two clients, so an event's name and its properties can't be
  each platform's own business: `subscription_prompt_shown` sent with `placement: 'highlight'` from
  the app would look like a different event forever, and nothing would fail to make that visible.
  These build the event; sending it is per platform, since the web fans out to PostHog, GA, Meta and
  TikTok in the browser, and the app has its own transport.

  Web-only events — the push and email funnels, which need browser APIs to even happen — stay in
  `apps/web/app/lib/analytics-events.ts` alongside these.

  Names are snake_case, matching the events already in the warehouse.
*/

import { simpleDateToDate } from './dates';
import type { Community, HeardAboutAnswer, SolvedCTAKind } from './solvedCta';
import type { ManageTarget, SubscriptionProvider } from './subscription';
import type { GameType, SimpleDate } from './types';

/**
 * How many times an event may ever be sent from one install.
 *
 * Stated by the event rather than by the caller: "first ever move" means the same thing on both
 * clients, and a limit that lives at the call site is one each new call site has to remember. See
 * `createEventTracker`, which is what enforces it.
 */
export interface EventLimit {
  /** The counter's name — what distinguishes "once ever" from "once for this board". */
  key: string;
  /** Emissions allowed. One unless it says otherwise. */
  times?: number;
}

/** One event, ready for whatever sends it. */
export interface AnalyticsEvent {
  name: string;
  properties: Record<string, unknown>;
  /** Absent on an event that may be sent every time it happens, which is most of them. */
  limit?: EventLimit;
}

/** How an account was reached. */
export type AuthMethod = 'email' | 'google' | 'apple';

/** Where a non-subscriber met a subscription prompt. */
export type SubscriptionPromptPlacement = 'highlighter' | 'archive' | 'challenge';

export const loginStarted = (): AnalyticsEvent => ({ name: 'login_started', properties: {} });

export const loginCompleted = (method: AuthMethod): AnalyticsEvent => ({
  name: 'login_completed',
  properties: { method },
});

export const signupSuccess = (method: AuthMethod): AnalyticsEvent => ({
  name: 'signup_success',
  properties: { method },
});

/** `gameType` is absent where the prompt isn't about one game, as on the weekly challenges' own page. */
export const subscriptionPromptShown = (
  gameType: GameType | undefined,
  placement: SubscriptionPromptPlacement
): AnalyticsEvent => ({
  name: 'subscription_prompt_shown',
  properties: { game_type: gameType, placement },
});

export const subscriptionPromptClicked = (
  gameType: GameType | undefined,
  placement: SubscriptionPromptPlacement
): AnalyticsEvent => ({
  name: 'subscription_prompt_clicked',
  properties: { game_type: gameType, placement },
});

/**
 * A purchase went through. `source` is who took the payment, `key` the product bought — the store
 * product ids and the website's price keys are the same names — and `amount` its price in `currency`.
 */
export const subscriptionSuccess = (
  source: SubscriptionProvider,
  key: string,
  amount: number,
  currency: string
): AnalyticsEvent => ({
  name: 'subscription_success',
  properties: { source, amount, currency, key },
});

/**
 * A subscriber in a failed-payment grace period was shown the warning. `target` is where its button
 * sends them to fix it, which follows from how they subscribed.
 */
export const paymentFailedSeen = (target: ManageTarget): AnalyticsEvent => ({
  name: 'payment_failed_seen',
  properties: { target },
});

/** They pressed the warning's button to go and fix their billing. */
export const paymentFailedClicked = (target: ManageTarget): AnalyticsEvent => ({
  name: 'payment_failed_clicked',
  properties: { target },
});

/**
 * The daily-reminders email was switched on or off. The same event as the website's
 * `trackDailyEmailEnabled`, which also fires from its signup and solved screens.
 */
export const dailyEmailEnabled = (source: string, optedIn: boolean): AnalyticsEvent => ({
  name: 'daily_email_enabled',
  properties: { source, opted_in: optedIn },
});

/**
 * An attempt to turn the daily reminder on, once the permission prompt has settled. The website's
 * `push_enabled`, with the app's platforms alongside its `ios_standalone` and `other`.
 */
export const pushEnabled = (
  source: string,
  platform: 'ios_app' | 'android_app',
  result: 'subscribed' | 'denied' | 'error'
): AnalyticsEvent => ({
  name: 'push_enabled',
  properties: { source, platform, result },
});

/** The daily reminder was turned off from a settings control. The website's `push_disabled`. */
export const pushDisabled = (source: string): AnalyticsEvent => ({ name: 'push_disabled', properties: { source } });

/**
 * A reminder notification was tapped. `campaign` names the notification; `already_open` is true when
 * the tap reached a website tab or an app that was already running, rather than starting one.
 */
export const pushNotificationClicked = (campaign: string, alreadyOpen: boolean): AnalyticsEvent => ({
  name: 'push_notification_clicked',
  properties: { campaign, already_open: alreadyOpen },
});

/**
 * The subscribe page or the app's paywall was opened. The same shape as the website's
 * `trackPaywallViewed`, whose `resumed` is the return trip from a signup detour the app doesn't make.
 */
export const paywallViewed = (source: string, gameType?: GameType): AnalyticsEvent => ({
  name: 'paywall_viewed',
  properties: gameType ? { source, game_type: gameType, resumed: false } : { source, resumed: false },
});

/**
 * The solved screen's rotating CTA showed one of its asks (see solvedCta). The denominator for each ask's
 * own conversion event: signupwall_viewed with source 'solved', paywall_viewed with source 'solved_cta',
 * daily_email_enabled with source 'solved', push_enabled with source 'solved_cta',
 * solved_cta_community_clicked and heard_about_submitted.
 */
export const solvedCTAShown = (cta: SolvedCTAKind): AnalyticsEvent => ({
  name: 'solved_cta_shown',
  properties: { cta },
});

/** A link in the solved screen's community ask was followed. */
export const solvedCTACommunityClicked = (community: Community): AnalyticsEvent => ({
  name: 'solved_cta_community_clicked',
  properties: { community },
});

/** The solved screen's "How did you hear about Inkwell?" was answered. */
export const heardAboutSubmitted = (answer: HeardAboutAnswer): AnalyticsEvent => ({
  name: 'heard_about_submitted',
  properties: { ...answer },
});

/** The comments page for a day's puzzle was opened. */
export const commentsOpened = (gameType: GameType, boardDate: SimpleDate): AnalyticsEvent => ({
  name: 'comments_opened',
  properties: { game_type: gameType, puzzle_date: boardDate },
});

/** A comment was posted, or with `isReply` a reply to one. `hasPuzzle` is the author's board going with it. */
export const commentPosted = (
  gameType: GameType,
  boardDate: SimpleDate,
  isReply: boolean,
  hasPuzzle: boolean
): AnalyticsEvent => ({
  name: 'comment_posted',
  properties: { game_type: gameType, puzzle_date: boardDate, is_reply: isReply, has_puzzle: hasPuzzle },
});

/** A reaction was added to a comment. Taking one back sends nothing. */
export const commentReactionAdded = (gameType: GameType, boardDate: SimpleDate, emoji: string): AnalyticsEvent => ({
  name: 'comment_reaction_added',
  properties: { game_type: gameType, puzzle_date: boardDate, emoji },
});

/** Where a screen name was chosen: the account page, or the composer on the way to a first comment. */
export type ScreenNameSource = 'account' | 'composer';

/** A screen name was chosen or changed. */
export const screenNameSet = (source: ScreenNameSource): AnalyticsEvent => ({
  name: 'screen_name_set',
  properties: { source },
});

/** What identifies a play, on both events below. */
export interface PuzzleEventProps {
  gameType: GameType;
  boardId: number;
  /** Absent on the practice and tutorial boards, which have a slug rather than a date, and on challenges. */
  boardDate?: SimpleDate;
  /** The Monday of a weekly challenge's week; absent for every other board. What marks a challenge's
   * events apart from a daily's, since they're otherwise the same events. */
  challengeWeek?: SimpleDate;
  /** Absent for a player who has never been given one — an anonymous first visit. */
  userStr?: string;
}

const puzzleProps = ({ gameType, boardId, boardDate, challengeWeek, userStr }: PuzzleEventProps) => ({
  game_type: gameType,
  board_id: boardId,
  puzzle_date: boardDate,
  challenge_week: challengeWeek,
  user_str: userStr,
});

/**
 * A board was opened. `resumed` is a play picked up from a save rather than begun, and `replay` counts
 * how many times this board has been finished before.
 */
export const puzzleStarted = (
  props: PuzzleEventProps,
  { resumed, replay, tutorial }: { resumed: boolean; replay: number; tutorial: boolean }
): AnalyticsEvent => ({
  name: tutorial ? 'tutorial_started' : 'puzzle_started',
  properties: { ...puzzleProps(props), resumed, replay },
});

/** A board was solved. The stats are the ones the play itself accumulated. */
export const puzzleCompleted = (
  props: PuzzleEventProps,
  { playDuration, hintsUsed, checksUsed, pauseCount, tutorial }: PuzzleCompletionStats
): AnalyticsEvent => ({
  name: tutorial ? 'tutorial_completion' : 'puzzle_completion',
  properties: {
    ...puzzleProps(props),
    completion_time: playDuration,
    hints_used: hintsUsed,
    checks_used: checksUsed,
    pause_count: pauseCount,
  },
});

/** The stats a finished play carries. */
export interface PuzzleCompletionStats {
  playDuration: number;
  hintsUsed: number;
  checksUsed: number;
  pauseCount: number;
  tutorial: boolean;
}

/**
 * The player's first move ever, anywhere — an account-level conversion rather than a puzzle event,
 * which is why it's limited to one emission for the life of the install.
 */
export const firstEverMove = (): AnalyticsEvent => ({
  name: 'first_ever_move',
  properties: {},
  limit: { key: 'firstEverMove' },
});

/** The same, for the first puzzle they ever finish. Carries the completion's own properties. */
export const firstEverCompletion = (props: PuzzleEventProps, stats: PuzzleCompletionStats): AnalyticsEvent => ({
  ...puzzleCompleted(props, { ...stats, tutorial: false }),
  name: 'first_ever_completion',
  limit: { key: 'firstEverCompletion' },
});

/**
 * The counter prefix for one game's move events on a given weekday.
 *
 * Keyed by game even though the event no longer is: the limit is one emission per game per board, so
 * a player who opens both stars and fields on the same date reports a move for each.
 *
 * The weekday is in the key, not the date, so a sweep leaves at most seven counters behind: each new
 * date drops the previous one that fell on the same day. `first_3_` is historical — the event once
 * covered the first three moves — and is kept because renaming it would re-fire the event for
 * everyone who has already sent it.
 */
export const gameMoveCounterPrefix = (gameType: GameType, boardDate: SimpleDate): string =>
  `first_3_${gameType}_moves_${simpleDateToDate(boardDate).getDay()}_`;

/**
 * A move on a dated board, sent once per board — the signal is that a player engaged with the day's
 * puzzle at all, so every move after the first says nothing new.
 *
 * One event for every game, with the game in `game_type`, as `puzzle_completion` already does: a
 * chart of "did they play" is then a chart of one event name, and adding a game doesn't mean
 * revisiting every saved insight to add `<game>_move` to it. It identifies the play the same way
 * too, bar `user_str`, which a move has no way to know — only the start and the completion are
 * handed the user.
 *
 * `move` is absent when the change was to the board as a whole rather than to one square: a drag or
 * swipe publishes its whole path in one update, and loopy-river has no single-cell move at all. The
 * square is decoration on an event that only says "they played", so a move without one still counts
 * — on touch a drag is routinely the first thing a player does, and with one emission per board,
 * dropping that move drops the board.
 */
export const gameMove = (
  gameType: GameType,
  boardDate: SimpleDate,
  boardId: number,
  move?: { row: number; col: number; cell: unknown }
): AnalyticsEvent => ({
  name: 'puzzle_move',
  properties: {
    game_type: gameType,
    board_id: boardId,
    puzzle_date: boardDate,
    ...(move && { cell_value: move.cell, row: move.row, col: move.col }),
  },
  limit: { key: `${gameMoveCounterPrefix(gameType, boardDate)}${boardDate}` },
});
